'use client';

import React, { useState, useEffect } from 'react';
import { InformationCircleIcon, ChevronLeftIcon, ChevronRightIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const ASSESSMENT_DURATION_MINUTES = 30;
const ASSESSMENT_DURATION_SECONDS = ASSESSMENT_DURATION_MINUTES * 60;

function Timer({ resetKey, isRunning, onTimeUp }: { resetKey: number; isRunning: boolean; onTimeUp: () => void }) {
    const [timeLeft, setTimeLeft] = useState(ASSESSMENT_DURATION_SECONDS);

    useEffect(() => {
        setTimeLeft(ASSESSMENT_DURATION_SECONDS); // Reset timer when resetKey changes
    }, [resetKey]);

    useEffect(() => {
        if (!isRunning) return;

        const timer = setInterval(() => {
            setTimeLeft((prevTime) => {
                if (prevTime <= 0) {
                    clearInterval(timer);
                    onTimeUp();
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [isRunning, onTimeUp]);

    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;

    return (
        <div className="flex items-center justify-center gap-2 text-sm font-medium">
            <div className="flex items-center gap-1.5">
                <div className={`h-2 w-2 rounded-full ${isRunning ? 'bg-orange-500 animate-pulse' : 'bg-gray-300'}`} />
                <span className="text-gray-900">
                    {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
                </span>
            </div>
            <span className="text-gray-500">{isRunning ? 'remaining' : 'not started'}</span>
        </div>
    );
}

function HelpSection() {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="fixed bottom-4 right-4">
            {isOpen ? (
                <div className="absolute bottom-0 right-full mr-2 bg-white rounded-lg border border-gray-100 shadow-lg p-4 w-64">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <InformationCircleIcon className="h-5 w-5 text-gray-500" />
                            <h2 className="text-sm font-medium text-gray-900">Need Help?</h2>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                    <ul className="space-y-2">
                        <li>
                            <a
                                href="https://docs.preswald.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-700 transition-colors block"
                            >
                                Check the Docs
                            </a>
                        </li>
                        <li>
                            <a
                                href="https://github.com/StructuredLabs/preswald/issues"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-700 transition-colors block"
                            >
                                Ask a question via GitHub Issues
                            </a>
                        </li>
                        <li>
                            <a
                                href="https://join.slack.com/t/structuredlabs-users/shared_invite/zt-33zwhyv3l-6Xu4bHL6b6~bI3z9fvlUig"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-700 transition-colors block"
                            >
                                Join our Slack Community
                            </a>
                        </li>
                    </ul>
                </div>
            ) : null}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            >
                <InformationCircleIcon className="h-3.5 w-3.5" />
                <span className="ml-2">Help</span>
            </button>
        </div>
    );
}

const BitflipPage: React.FC = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [resetKey, setResetKey] = useState(0);
    const [isTimerRunning, setIsTimerRunning] = useState(false);
    const [isTimeUp, setIsTimeUp] = useState(false);
    const totalSlides = 8; // Total number of content sections

    const goToPreviousSlide = () => {
        if (currentSlide > 1) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    const goToNextSlide = () => {
        if (currentSlide < totalSlides - 1) {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const handleRestart = () => {
        setCurrentSlide(0);
        setResetKey(prev => prev + 1);
        setIsTimerRunning(false);
        setIsTimeUp(false);
    };

    const handleStart = () => {
        setIsTimerRunning(true);
        goToNextSlide();
    };

    const handleTimeUp = () => {
        setIsTimeUp(true);
        setIsTimerRunning(false);
        setCurrentSlide(8); // Explicitly set to the time's up slide
    };

    const progress = ((currentSlide + 1) / totalSlides) * 100;

    return (
        <div className="min-h-screen py-6 sm:py-8 md:py-12 px-3 sm:px-4 md:px-6 lg:px-8 relative">
            {/* Timer in top right */}
            <div className="absolute top-2 sm:top-4 right-2 sm:right-4">
                <Timer resetKey={resetKey} isRunning={isTimerRunning} onTimeUp={handleTimeUp} />
            </div>

            <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6 md:space-y-8">
                {/* Progress Bar with Navigation */}
                <div className="flex items-center gap-2 sm:gap-4">
                    <button
                        onClick={goToPreviousSlide}
                        disabled={currentSlide <= 0 || isTimeUp}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-input bg-background text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                    >
                        <ChevronLeftIcon className="h-4 w-4" />
                    </button>

                    <div className="flex-1">
                        <div className="relative w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                                className="absolute h-full bg-gray-900 transition-all duration-300 ease-in-out"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>

                    <button
                        onClick={goToNextSlide}
                        disabled={currentSlide === 0 || currentSlide === totalSlides - 1 || isTimeUp}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-input bg-background text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                    >
                        <ChevronRightIcon className="h-4 w-4" />
                    </button>
                </div>

                {/* Slides Container */}
                <div className="relative">
                    <div className="bg-white rounded-lg border border-gray-100 p-4 sm:p-6 md:p-8 min-h-[500px] sm:min-h-[600px] md:min-h-[800px]">
                        {currentSlide === 0 && (
                            <div className="flex flex-col items-center justify-center h-full text-center">
                                <img
                                    src="/structuredlabs.png"
                                    alt="Structured Labs Logo"
                                    className="mb-6 w-48 object-contain"
                                    style={{
                                        animation: 'fadeInUp 0.5s ease-out forwards',
                                        opacity: 0,
                                        transform: 'translateY(20px)'
                                    }}
                                />
                                <h1 
                                    className="text-xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6"
                                    style={{
                                        animation: 'fadeInUp 0.5s ease-out forwards',
                                        animationDelay: '200ms',
                                        opacity: 0,
                                        transform: 'translateY(20px)'
                                    }}
                                >
                                   🎉 Online Assessment
                                </h1>
                                <p 
                                    className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 sm:mb-12 max-w-2xl"
                                    style={{
                                        animation: 'fadeInUp 0.5s ease-out forwards',
                                        animationDelay: '400ms',
                                        opacity: 0,
                                        transform: 'translateY(20px)'
                                    }}
                                >
                                    You've been invited to complete a short coding challenge. 
                                </p>
                                <button
                                    onClick={handleStart}
                                    className="mt-4 mb-8 inline-flex h-12 items-center justify-center rounded-full bg-primary px-10 text-base font-semibold text-primary-foreground ring-offset-background transition-all duration-300 hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                                    style={{
                                        animation: 'fadeInUp 0.5s ease-out forwards',
                                        animationDelay: '600ms',
                                        opacity: 0,
                                        transform: 'translateY(20px)',
                                        transition: 'all 0.3s ease-out'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                                        e.currentTarget.style.boxShadow = '0 10px 20px -5px rgba(var(--primary-rgb), 0.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                >
                                    <span className="relative">
                                        Start Assessment
                                        <span className="absolute -bottom-1 left-0 h-0.5 w-full origin-left scale-x-0 bg-white transition-transform duration-300 group-hover:scale-x-100"></span>
                                    </span>
                                </button>
                                <div 
                                    className="text-sm sm:text-base text-gray-600 mb-8 sm:mb-12 max-w-2xl"
                                    style={{
                                        animation: 'fadeInUp 0.5s ease-out forwards',
                                        animationDelay: '700ms',
                                        opacity: 0,
                                        transform: 'translateY(20px)'
                                    }}
                                >
                                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">What We're Evaluating:</h3>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Work with real-world datasets</li>
                                        <li>Perform data cleaning and SQL-like queries</li>
                                        <li>Build minimal data-driven UIs</li>
                                        <li>Use SDKs to compose end-user facing applications</li>
                                    </ul>
                                </div>
                                <p 
                                    className="mt-4 text-md text-gray-500 max-w-2xl"
                                    style={{
                                        animation: 'fadeInUp 0.5s ease-out forwards',
                                        animationDelay: '800ms',
                                        opacity: 0,
                                        transform: 'translateY(20px)'
                                    }}
                                >
                                    ⏰ You have {ASSESSMENT_DURATION_MINUTES} minutes to complete the assessment.
                                </p>
                                <p 
                                    className="mt-4 text-sm text-gray-300 max-w-2xl"
                                    style={{
                                        animation: 'fadeInUp 0.5s ease-out forwards',
                                        animationDelay: '800ms',
                                        opacity: 0,
                                        transform: 'translateY(20px)'
                                    }}
                                >
                                   Note that you can restart the assessment as many times as you'd like, but you will need to restart the assessment each time you want to try a different approach.
                                </p>
                                <style>
                                    {`
                                    @keyframes fadeInUp {
                                        from {
                                            opacity: 0;
                                            transform: translateY(20px);
                                        }
                                        to {
                                            opacity: 1;
                                            transform: translateY(0);
                                        }
                                    }
                                    `}
                                </style>
                            </div>
                        )}

                        {currentSlide === 1 && (
                            <div className="items-center justify-center text-center">
                                <h2 className="text-4xl sm:text-2xl font-semibold text-gray-900 mb-24">
                                    ⭐ 0: Show some support! Star Preswald on GitHub ⭐ 
                                </h2>
                                <a 
                                    href="https://github.com/StructuredLabs/preswald"
                                    className="text-2xl text-blue-600 hover:text-blue-700 transition-colors break-all border border-gray-200 rounded-lg p-8"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    https://github.com/StructuredLabs/preswald
                                </a>
                            </div>
                        )}

                        {currentSlide === 2 && (
                            <div>
                                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4 sm:mb-6">
                                    1. Setup environment
                                </h2>
                                <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">
                                    To simulate a real-world no-code/low-code environment, we're using <a href="https://preswald.com" className="text-blue-600 hover:text-blue-700 transition-colors">Preswald</a>, a platform for composing Python-backed data apps with UI.
                                </p>
                                <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">
                                    * Please use Google Chrome to complete this assessment.
                                </p>
                                <ol className="list-decimal list-inside text-base sm:text-lg text-gray-600 space-y-2 sm:space-y-3">
                                    <li>Go to <a href="https://preswald.com/dashboard" className="text-blue-600 hover:text-blue-700 transition-colors">https://preswald.com/dashboard</a></li>
                                    <li>Sign in using your GitHub account</li>
                                </ol>
                            </div>
                        )}

                        {currentSlide === 3 && (
                            <div>
                                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4 sm:mb-6">
                                    2. Choose a Dataset
                                </h2>
                                <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">
                                    Select/download a dataset from any of these sources:
                                </p>
                                <ul className="list-disc list-inside text-base sm:text-lg text-gray-600 space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                                    <li><a href="https://www.kaggle.com/datasets" className="text-blue-600 hover:text-blue-700 transition-colors">Kaggle Datasets</a></li>
                                    <li><a href="https://www.data.gov/" className="text-blue-600 hover:text-blue-700 transition-colors">Data.gov</a></li>
                                    <li><a href="https://github.com/awesomedata/awesome-public-datasets" className="text-blue-600 hover:text-blue-700 transition-colors">Open Data Repositories</a></li>
                                    <li>Any csv (e.g., weather, finance, sports stats)</li>
                                </ul>
                                <li>Click <strong className="font-semibold">"+ New Project"</strong> to create a new project.</li>
                                <li>Upload your dataset via the upload button in the top left corner.</li>
                            </div>
                        )}

                        {currentSlide === 4 && (
                            <div>
                                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4 sm:mb-6">
                                    3. Implement Your Preswald App
                                </h2>
                                <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">
                                    Modify <code className="bg-gray-100 px-2 py-1 rounded text-sm sm:text-base font-mono">Script</code> to include the following:
                                </p>
                                
                                <div className="bg-gray-50 p-4 sm:p-6 rounded-lg mb-4 sm:mb-6 border border-gray-100">
                                    <h3 className="font-medium text-gray-900 mb-2 sm:mb-3 text-base sm:text-lg">1. Load the dataset</h3>
                                    <pre className="bg-gray-900 text-gray-100 p-3 sm:p-4 md:p-6 rounded-lg overflow-x-auto text-sm sm:text-base font-mono">
                                        {`from preswald import connect, get_df

connect()  # Initialize connection to preswald.toml data sources
df = get_df("my_dataset")  # Load data`}
                                    </pre>
                                </div>

                                <div className="bg-gray-50 p-4 sm:p-6 rounded-lg mb-4 sm:mb-6 border border-gray-100">
                                    <h3 className="font-medium text-gray-900 mb-2 sm:mb-3 text-base sm:text-lg">2. Query or manipulate the data</h3>
                                    <pre className="bg-gray-900 text-gray-100 p-3 sm:p-4 md:p-6 rounded-lg overflow-x-auto text-sm sm:text-base font-mono">
                                        {`from preswald import query

sql = "SELECT * FROM my_dataset WHERE value > 50"
filtered_df = query(sql, "my_dataset")`}
                                    </pre>
                                </div>

                                <div className="bg-gray-50 p-4 sm:p-6 rounded-lg mb-4 sm:mb-6 border border-gray-100">
                                    <h3 className="font-medium text-gray-900 mb-2 sm:mb-3 text-base sm:text-lg">3. Build an interactive UI</h3>
                                    <pre className="bg-gray-900 text-gray-100 p-3 sm:p-4 md:p-6 rounded-lg overflow-x-auto text-sm sm:text-base font-mono">
                                        {`from preswald import table, text

text("# My Data Analysis App")
table(filtered_df, title="Filtered Data")`}
                                    </pre>
                                </div>

                                <div className="bg-gray-50 p-4 sm:p-6 rounded-lg mb-4 sm:mb-6 border border-gray-100">
                                    <h3 className="font-medium text-gray-900 mb-2 sm:mb-3 text-base sm:text-lg">4. Create a visualization</h3>
                                    <pre className="bg-gray-900 text-gray-100 p-3 sm:p-4 md:p-6 rounded-lg overflow-x-auto text-sm sm:text-base font-mono">
                                        {`from preswald import plotly
import plotly.express as px

fig = px.scatter(df, x="column1", y="column2", color="category")
plotly(fig)`}
                                    </pre>
                                </div>
                            </div>
                        )}

                        {currentSlide === 5 && (
                            <div>
                                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4 sm:mb-6">
                                    4. Customize the Branding
                                </h2>
                                <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">
                                    Edit the <code className="bg-gray-100 px-2 py-1 rounded text-sm sm:text-base font-mono">Config</code> file to customize the branding:
                                </p>
                                <ul className="list-disc list-inside text-base sm:text-lg text-gray-600 space-y-2 sm:space-y-3">
                                    <li>Change the title of the app</li>
                                    <li>Change the favicon</li>
                                    <li>(Optional) Change the logo (will be visibile if you use sidebar() component)</li>
                                    <li>(Optional) Change the color scheme (will be visibile if you use sidebar() component)</li>
                                </ul>
                            </div>
                        )}

                        {
                            currentSlide === 6 && !isTimeUp && (
                                <div>
                                    <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4 sm:mb-6">
                                        5. Publish your app
                                    </h2>
                                    <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">
                                        Click the <strong className="font-semibold">Publish</strong> button to publish your app.
                                        <br />
                                        <br />
                                        If you have trouble publishing, please download the app via the <strong className="font-semibold">Save</strong> button, upload it to a new GitHub repository, and save that repo link for your submission.
                                    </p>
                                </div>
                            )
                        }

                        {currentSlide === 7 && !isTimeUp && (
                            <div>
                                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4 sm:mb-6">
                                    6. Submit your finished app
                                </h2>
                                <div className="mt-4 sm:mt-6">
                                    <iframe 
                                        src="https://docs.google.com/forms/d/e/1FAIpQLSd53Sdg6SLuiRptEDk3zGyFspxobxOvrBPkM0hpSR3EPsNIYA/viewform?embedded=true" 
                                        width="100%" 
                                        height="500"
                                        className="sm:h-[600px] md:h-[700px]"
                                    >
                                        Loading...
                                    </iframe>
                                </div>
                            </div>
                        )}

                        {isTimeUp && (
                            <div className="flex flex-col items-center justify-center h-full text-center">
                                <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4 sm:mb-6">
                                    ⏰ Time's Up!
                                </h2>
                                <p className="text-base sm:text-lg text-gray-600 mb-8 sm:mb-12 max-w-2xl">
                                    You've run out of time for this assessment. Please click the restart button to try again.
                                </p>
                                <button
                                    onClick={handleRestart}
                                    className="inline-flex h-10 items-center justify-center rounded-md bg-destructive px-8 text-sm font-medium text-white ring-offset-background transition-colors hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                                >
                                    Restart Assessment
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Help Section */}
                <HelpSection />
            </div>

            {/* Restart button in bottom left */}
            <div className="fixed bottom-2 sm:bottom-4 left-2 sm:left-4">
                <button
                    onClick={handleRestart}
                    className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                >
                    <ArrowPathIcon className="h-3.5 w-3.5" />
                    <span className="ml-2">Restart</span>
                </button>
            </div>
        </div>
    );
};

export default BitflipPage; 